// dependencies
import { QueryClientContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useSyncExternalStore } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { NAVIGATION_CONFIG_QUERY_KEY, navigationConfigQuery } from "../../../api/queries/defs/navigation";

// api
import { type NavigationConfig, navigationConfigResponseSchema } from "../../services/defs/navigation";
import { PYUseQueryOptions } from "../StandardQueryArguments";

export type { NavigationConfig };

const OBJECT_ID_PATTERN = "[0-9a-fA-F]{24}";
const EMPLOYEE_CONTEXT_PATH_PATTERNS = [
	new RegExp(`^/employee-page/(${OBJECT_ID_PATTERN})(?:/|$)`),
	new RegExp(`^/reviews/(${OBJECT_ID_PATTERN})(?:/|$)`),
	new RegExp(`^/feedback/(${OBJECT_ID_PATTERN})(?:/|$)`),
	new RegExp(`^/documents/(${OBJECT_ID_PATTERN})(?:/|$)`),
	new RegExp(`^/employees/(${OBJECT_ID_PATTERN})/goals(?:/|$)`),
];

export const getEmployeeIdFromPathname = (pathname: string): string | undefined => {
	for (const pattern of EMPLOYEE_CONTEXT_PATH_PATTERNS) {
		const match = pathname.match(pattern);
		if (match?.[1]) return match[1];
	}
	return undefined;
};

/**
 * Fetches navigation config for the current viewing context.
 *
 * The default `"route"` scope follows the employee in the route params;
 * `"viewer"` always fetches the logged-in user's own config (PY-12717).
 */
export const useNavigationConfig = (
	options?: PYUseQueryOptions<z.infer<typeof navigationConfigResponseSchema>>,
	scope: "route" | "viewer" = "route"
) => {
	const params = useParams();
	const queryClient = useQueryClient();

	const routeEmployeeId =
		(params?.userID as string | undefined) ||
		(params?.userId as string | undefined) ||
		(params?.employeeID as string | undefined);
	const pathEmployeeId =
		typeof window !== "undefined" ? getEmployeeIdFromPathname(window.location.pathname) : undefined;
	const employeeId = scope === "viewer" ? undefined : routeEmployeeId || pathEmployeeId;

	const result = useQuery<z.infer<typeof navigationConfigResponseSchema>>(navigationConfigQuery(employeeId, options));

	// If the fetched data is for the current user viewing themselves,
	// populate the cache for both "self" and the user ID to prevent flashing
	useEffect(() => {
		if (result.data?.navigationConfig) {
			const { currentUserId, theUserId } = result.data.navigationConfig;

			// If viewing self, populate both cache keys
			if (currentUserId === theUserId) {
				// Set cache for "self" key
				queryClient.setQueryData([NAVIGATION_CONFIG_QUERY_KEY, "self"], result.data);

				// Set cache for the actual user ID key
				queryClient.setQueryData([NAVIGATION_CONFIG_QUERY_KEY, theUserId], result.data);
			}
		}
	}, [result.data, queryClient]);

	return result;
};

// Reads preferredNavigation from any cached navigation-config entry — it's a
// company-wide value, so the viewed-employee entry is authoritative when "self"
// hasn't been populated (e.g. landing on /user/:userID via an avatar click).
export const useIsNewNavigation = (): boolean => {
	const queryClient = useContext(QueryClientContext);

	const subscribe = useCallback(
		(onChange: () => void) => {
			if (!queryClient) return () => {};
			return queryClient.getQueryCache().subscribe((event) => {
				if (event.query.queryKey[0] === NAVIGATION_CONFIG_QUERY_KEY) {
					onChange();
				}
			});
		},
		[queryClient]
	);

	const getSnapshot = useCallback(() => {
		if (queryClient) {
			const selfCached = queryClient.getQueryData<z.infer<typeof navigationConfigResponseSchema>>([
				NAVIGATION_CONFIG_QUERY_KEY,
				"self",
			]);
			const entries = queryClient.getQueriesData<z.infer<typeof navigationConfigResponseSchema>>({
				queryKey: [NAVIGATION_CONFIG_QUERY_KEY],
			});
			const hasNewEntry =
				selfCached?.navigationConfig?.preferredNavigation === "new" ||
				entries.some(([, data]) => data?.navigationConfig?.preferredNavigation === "new");
			if (hasNewEntry) return true;
			const hasLoadedEntry =
				selfCached?.navigationConfig?.preferredNavigation !== undefined ||
				entries.some(([, data]) => data?.navigationConfig?.preferredNavigation !== undefined);
			if (hasLoadedEntry) return false;
		}
		return typeof document !== "undefined" && document.body?.dataset.layout === "sidenav";
	}, [queryClient]);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
